import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  Theme,
  getStoredTheme,
  setStoredTheme,
  applyTheme,
  getResolvedTheme,
  getSystemTheme,
  watchSystemTheme
} from '@/utils/theme';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'system'
}) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Initialize with stored theme or default
    const stored = getStoredTheme();
    return stored || defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    const initialTheme = getStoredTheme() || defaultTheme;
    return getResolvedTheme(initialTheme);
  });

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    setStoredTheme(newTheme);

    const resolved = getResolvedTheme(newTheme);
    setResolvedTheme(resolved);
    applyTheme(resolved);
  };

  // Listen to system theme changes when theme is set to 'system'
  useEffect(() => {
    if (theme !== 'system') return;

    // Primary: Event listener for immediate theme changes
    const unsubscribe = watchSystemTheme(systemTheme => {
      setResolvedTheme(systemTheme);
      applyTheme(systemTheme);
    });

    // Fallback: Periodic polling for scheduled OS theme switches
    // macOS time-based auto dark mode often doesn't trigger the change event
    const checkInterval = setInterval(() => {
      const currentSystemTheme = getSystemTheme();
      setResolvedTheme(prev => {
        if (currentSystemTheme !== prev) {
          applyTheme(currentSystemTheme);
          return currentSystemTheme;
        }
        return prev;
      });
    }, 60000); // Check every minute

    return () => {
      unsubscribe();
      clearInterval(checkInterval);
    };
  }, [theme]);

  // Re-check system theme when user returns to tab (visibility change)
  useEffect(() => {
    if (theme !== 'system') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const currentSystemTheme = getSystemTheme();
        setResolvedTheme(prev => {
          if (currentSystemTheme !== prev) {
            applyTheme(currentSystemTheme);
            return currentSystemTheme;
          }
          return prev;
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [theme]);

  // Apply theme on mount
  useEffect(() => {
    const resolved = getResolvedTheme(theme);
    setResolvedTheme(resolved);
    applyTheme(resolved);
  }, []);

  const value: ThemeContextType = {
    theme,
    resolvedTheme,
    setTheme
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};
